import React, { useState, useCallback } from 'react';
import { useAdminSupportedStates } from '../../agents/supportedStatesAgent';
import './SupportedStatesManager.css';

function StateRow({ state, onToggle, onSaveNotes, busy }) {
  const [notes, setNotes] = useState(state.notes || '');
  const [editing, setEditing] = useState(false);

  const handleSave = async () => {
    await onSaveNotes(state.code, notes);
    setEditing(false);
  };

  return (
    <div className={`state-row ${state.enabled ? 'is-enabled' : ''}`}>
      <div className="state-row-main">
        <span className="state-code">{state.code}</span>
        <div className="state-meta">
          <span className="state-name">{state.name}</span>
          {state.notes && !editing && (
            <span className="state-notes">{state.notes}</span>
          )}
        </div>
        <label className="state-toggle">
          <input
            type="checkbox"
            checked={state.enabled}
            disabled={busy}
            onChange={(e) => onToggle(state.code, e.target.checked, state.notes)}
            aria-label={`Enable document generation for ${state.name}`}
          />
          <span className="state-toggle-track">
            <span className="state-toggle-thumb" />
          </span>
          <span className="state-toggle-label">
            {state.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </label>
      </div>

      {editing ? (
        <div className="state-notes-editor">
          <textarea
            className="state-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes (e.g. legal review status, template version)"
            rows={2}
            maxLength={500}
          />
          <div className="state-notes-actions">
            <button
              className="admin-btn admin-btn--add"
              onClick={handleSave}
              disabled={busy}
            >
              Save
            </button>
            <button
              className="admin-btn admin-btn--remove"
              onClick={() => { setNotes(state.notes || ''); setEditing(false); }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          className="state-edit-link"
          onClick={() => setEditing(true)}
          disabled={busy}
        >
          {state.notes ? 'Edit notes' : 'Add notes'}
        </button>
      )}
    </div>
  );
}

export default function SupportedStatesManager() {
  const { available, loading, error, isAdmin, updateState } = useAdminSupportedStates();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [filter, setFilter] = useState('');

  const handleToggle = useCallback(
    async (code, enabled, notes) => {
      setBusy(true);
      setActionError(null);
      try {
        await updateState(code, { enabled, notes });
      } catch (err) {
        setActionError(err?.message || 'Failed to update state');
      } finally {
        setBusy(false);
      }
    },
    [updateState]
  );

  const handleSaveNotes = useCallback(
    async (code, notes) => {
      setBusy(true);
      setActionError(null);
      try {
        // We need the current enabled flag — find from `available`.
        const row = available.find((s) => s.code === code);
        await updateState(code, { enabled: !!row?.enabled, notes });
      } catch (err) {
        setActionError(err?.message || 'Failed to save notes');
      } finally {
        setBusy(false);
      }
    },
    [updateState, available]
  );

  if (loading) {
    return (
      <div className="admin-shell">
        <div className="admin-loading">Loading supported states...</div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="admin-shell">
        <div className="admin-forbidden">
          <h2 className="admin-forbidden-title">Access Denied</h2>
          <p>You do not have permission to manage supported states.</p>
        </div>
      </div>
    );
  }

  const enabledCount = available.filter((s) => s.enabled).length;
  const filterLower = filter.trim().toLowerCase();
  const visible = filterLower
    ? available.filter(
        (s) =>
          s.code.toLowerCase().includes(filterLower) ||
          s.name.toLowerCase().includes(filterLower)
      )
    : available;

  const enabledStates = visible.filter((s) => s.enabled);
  const otherStates = visible.filter((s) => !s.enabled);

  return (
    <div className="admin-shell">
      <div className="admin-header">
        <h1 className="admin-title">Document States</h1>
        <p className="admin-subtitle">
          Choose which U.S. states the platform can generate transaction documents for.
          Disabled states show as &ldquo;coming soon&rdquo; throughout the app.
        </p>
      </div>

      {(error || actionError) && (
        <div className="admin-banner admin-banner--error">
          {actionError || error}
        </div>
      )}

      <div className="states-summary">
        <span className="states-summary-pill">
          {enabledCount} of {available.length} states enabled
        </span>
        <input
          className="admin-input states-filter"
          type="search"
          placeholder="Search states by code or name..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter states"
        />
      </div>

      <section className="admin-section">
        <h2 className="admin-section-title">Enabled</h2>
        <p className="admin-section-desc">
          Documents can be generated for offers in these states.
        </p>
        {enabledStates.length === 0 ? (
          <p className="admin-empty">
            No states are currently enabled — document generation is fully disabled.
          </p>
        ) : (
          <div className="state-list">
            {enabledStates.map((s) => (
              <StateRow
                key={s.code}
                state={s}
                onToggle={handleToggle}
                onSaveNotes={handleSaveNotes}
                busy={busy}
              />
            ))}
          </div>
        )}
      </section>

      <section className="admin-section">
        <h2 className="admin-section-title">All Other States</h2>
        <p className="admin-section-desc">
          Enable a state once its templates and legal requirements have been reviewed.
        </p>
        {otherStates.length === 0 ? (
          <p className="admin-empty">No other states match your filter.</p>
        ) : (
          <div className="state-list">
            {otherStates.map((s) => (
              <StateRow
                key={s.code}
                state={s}
                onToggle={handleToggle}
                onSaveNotes={handleSaveNotes}
                busy={busy}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
